import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { storableError } from '../../util/errors';
import * as api from '../../util/api';

const PAGE_SIZE = 50;

const initialState = {
  messages: [],
  total: 0,
  page: 1,
  totalPages: 0,
  fetchMessagesInProgress: false,
  fetchMessagesError: null,
  sendMessageInProgress: false,
  sendMessageError: null,
};

export const fetchMessages = createAsyncThunk(
  'BroadcastChatPage/fetchMessages',
  (page, { rejectWithValue }) => {
    return api.fetchBroadcastMessages(page)
      .then(data => data)
      .catch(e => rejectWithValue(storableError(e)));
  }
);

export const sendMessage = createAsyncThunk(
  'BroadcastChatPage/sendMessage',
  (content, { getState, dispatch, rejectWithValue }) => {
    const state = getState();
    const currentUser = state.user.currentUser;
    if (!currentUser) {
      return rejectWithValue(storableError(new Error('User not logged in')));
    }
    const senderId = currentUser.id.uuid;
    return api.sendBroadcastMessage({ content, senderId })
      .then(() => dispatch(fetchMessages(1)))
      .catch(e => rejectWithValue(storableError(e)));
  }
);

const BroadcastChatPage = createSlice({
  name: 'BroadcastChatPage',
  initialState,
  reducers: {
    clearMessages(state) {
      state.messages = [];
      state.total = 0;
      state.page = 1;
      state.totalPages = 0;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchMessages.pending, state => {
        state.fetchMessagesInProgress = true;
        state.fetchMessagesError = null;
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        state.fetchMessagesInProgress = false;
        const newMessages = action.payload.messages || [];
        const existingMessages = state.messages || [];
        
        // Merge messages and remove duplicates
        const allMessages = [...existingMessages, ...newMessages];
        const uniqueMessages = Array.from(new Map(allMessages.map(m => [m._id || m.id, m])).values());
        
        // Sort chronologically
        state.messages = uniqueMessages.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        
        state.total = action.payload.total;
        state.page = action.payload.page;
        state.totalPages = action.payload.totalPages;
      })
      .addCase(fetchMessages.rejected, (state, action) => {
        state.fetchMessagesInProgress = false;
        state.fetchMessagesError = action.payload;
      })
      .addCase(sendMessage.pending, state => {
        state.sendMessageInProgress = true;
        state.sendMessageError = null;
      })
      .addCase(sendMessage.fulfilled, state => {
        state.sendMessageInProgress = false;
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.sendMessageInProgress = false;
        state.sendMessageError = action.payload;
      });
  },
});

export const { clearMessages } = BroadcastChatPage.actions;

export const loadData = (params, search, config) => dispatch => {
  return dispatch(fetchMessages(1));
};

export default BroadcastChatPage.reducer;
